import { useSyncExternalStore } from 'react';

/** Farbschema: 'light' | 'dark' | 'system' (folgt dem Betriebssystem). Gespeichert pro Browser. */
export const THEME_STORAGE_KEY = 'blimo-theme';
export const THEMES = ['light', 'dark', 'system'];

const listeners = new Set();
const media = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null);

export function getTheme() {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(value) ? value : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(theme = getTheme()) {
  return theme === 'system' ? (media()?.matches ? 'dark' : 'light') : theme;
}

export function applyTheme(theme = getTheme()) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolveTheme(theme) === 'dark');
}

export function setTheme(theme) {
  try {
    if (theme === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Speicher nicht verfügbar (privater Modus): Wahl gilt nur für diese Sitzung
  }
  applyTheme(theme);
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  const mql = media();
  const onSystem = () => {
    applyTheme();
    listener();
  };
  const onStorage = (e) => {
    if (e.key === THEME_STORAGE_KEY) onSystem();
  };
  mql?.addEventListener?.('change', onSystem);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    mql?.removeEventListener?.('change', onSystem);
    window.removeEventListener('storage', onStorage);
  };
}

/** @returns {{ theme: string, resolvedTheme: 'light'|'dark', setTheme: (t: string) => void }} */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getTheme, () => 'system');
  const resolvedTheme = useSyncExternalStore(
    subscribe,
    () => resolveTheme(theme),
    () => 'light',
  );
  return { theme, resolvedTheme, setTheme };
}
